import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Phone, MapPin, Building2, Globe, Users, DollarSign, TrendingUp, Check, ArrowRight, ArrowLeft, Shield, LogOut } from 'lucide-react';

export default function Onboarding() {
  const { profile, updateProfile, refreshProfile, impersonationContext, signOut } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exitingImpersonation, setExitingImpersonation] = useState(false);
  
  // Form state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [country, setCountry] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [domain, setDomain] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [currency, setCurrency] = useState('ZAR');
  const [monthlyRevenue, setMonthlyRevenue] = useState('');

  useEffect(() => {
    // Pre-fill any existing profile data
    if (profile) {
      setPhoneNumber(profile?.phoneNumber || '');
      setCountry(profile?.country || '');
      setBusinessName(profile?.businessName || '');
      setDomain(profile?.domain || '');
      setEmployeeCount(profile?.employeeCount?.toString() || '');
      setCurrency(profile?.currency || 'ZAR');
      setMonthlyRevenue(profile?.monthlyRevenue?.toString() || '');
    }
  }, [profile]);

  const totalSteps = 3;

  const handleExitImpersonation = async () => {
    try {
      setExitingImpersonation(true);
      
      // Call signOut which will handle impersonation cleanup
      await signOut();
      
      // Navigate to admin dashboard
      navigate('/admin-dashboard');
      
      // Force reload to ensure clean state
      window.location?.reload();
    } catch (error) {
      console.error('Error exiting impersonation:', error);
      setError('Failed to exit impersonation. Please try again.');
    } finally {
      setExitingImpersonation(false);
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!phoneNumber?.trim()) {
          setError('Please enter your phone number');
          return false;
        }
        if (!country?.trim()) {
          setError('Please enter your country');
          return false;
        }
        break;
      case 2:
        if (!businessName?.trim()) {
          setError('Please enter your business name');
          return false;
        }
        if (!domain?.trim()) {
          setError('Please enter your domain');
          return false;
        }
        break;
      case 3:
        if (!employeeCount || parseInt(employeeCount) < 1) {
          setError('Please enter a valid employee count');
          return false;
        }
        break;
      default:
        break;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  const handleComplete = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const profileData = {
        phoneNumber,
        country,
        businessName,
        domain,
        employeeCount: parseInt(employeeCount),
        currency,
        monthlyRevenue: monthlyRevenue ? parseFloat(monthlyRevenue) : null,
      };

      await updateProfile(profileData);
      await refreshProfile();
      
      // Navigate to main dashboard
      navigate('/');
    } catch (err) {
      console.error('Onboarding error:', err);
      setError(err?.message || 'Failed to save your information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Navigate directly to dashboard without completing onboarding
    navigate('/');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Contact Information</h3>
              <p className="text-gray-600 text-sm">Let's start with your contact details</p>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="+27 12 345 6789"
                />
              </div>
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="South Africa"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Business Information</h3>
              <p className="text-gray-600 text-sm">Tell us about your business</p>
            </div>

            {/* Business Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Acme Corporation"
                />
              </div>
            </div>

            {/* Domain */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Domain <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Globe className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="example.com"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Financial Details</h3>
              <p className="text-gray-600 text-sm">Help us understand your business scale</p>
            </div>

            {/* Employee Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee Count <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={employeeCount}
                  onChange={(e) => setEmployeeCount(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="10"
                  min="1"
                />
              </div>
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Currency <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="ZAR">South African Rand (ZAR)</option>
                  <option value="USD">US Dollar (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                  <option value="GBP">British Pound (GBP)</option>
                  <option value="AUD">Australian Dollar (AUD)</option>
                  <option value="CAD">Canadian Dollar (CAD)</option>
                </select>
              </div>
            </div>

            {/* Monthly Revenue (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Revenue <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <TrendingUp className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={monthlyRevenue}
                  onChange={(e) => setMonthlyRevenue(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="50000"
                  min="0"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-900 via-blue-900 to-cyan-900 flex items-center justify-center p-4">
      {/* CRITICAL: Admin Impersonation Warning Banner */}
      {impersonationContext && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-black px-6 py-3 flex items-center justify-between z-50 shadow-lg">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5" />
            <div>
              <p className="font-bold text-sm">⚠️ ADMIN IMPERSONATION ACTIVE</p>
              <p className="text-xs">
                Viewing as: <strong>{profile?.fullName}</strong> ({profile?.email}) | 
                Admin: {impersonationContext?.adminEmail}
              </p>
            </div>
          </div>
          <button
            onClick={handleExitImpersonation}
            disabled={exitingImpersonation}
            className="flex items-center gap-2 px-4 py-2 bg-black text-yellow-500 rounded-lg hover:bg-gray-800 transition-colors font-semibold disabled:opacity-50"
          >
            <LogOut className="w-4 h-4" />
            {exitingImpersonation ? 'Exiting...' : 'Exit Impersonation'}
          </button>
        </div>
      )}
      <div className={`max-w-2xl w-full ${impersonationContext ? 'mt-20' : ''}`}>
        {/* Header */}
        <div className="text-center mb-8 relative">
          <h1 className="text-3xl font-bold text-white mb-2">Complete Your Profile</h1>
          <p className="text-teal-100">
            Welcome! Let's set up your account to get you started
          </p>
          
          {/* Small Hidden Skip Button - Only show if NOT impersonating */}
          {!impersonationContext && (
            <button
              onClick={handleSkip}
              className="absolute top-0 right-0 text-[10px] text-teal-200 hover:text-teal-100 opacity-40 hover:opacity-70 transition-opacity underline"
              title="Skip onboarding"
            >
              skip
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            {[1, 2, 3]?.map((step) => (
              <React.Fragment key={step}>
                <div className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                      currentStep > step
                        ? 'bg-teal-600 text-white'
                        : currentStep === step
                        ? 'bg-teal-600 text-white' :'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {currentStep > step ? <Check className="w-5 h-5" /> : step}
                  </div>
                  <span className="ml-2 text-sm font-medium text-gray-700 hidden sm:inline">
                    {step === 1 ? 'Contact' : step === 2 ? 'Business' : 'Financial'}
                  </span>
                </div>
                {step < totalSteps && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      currentStep > step ? 'bg-teal-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          {/* Show impersonation warning in form */}
          {impersonationContext && (
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-yellow-700 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-900 text-sm">Admin Impersonation Mode</p>
                  <p className="text-yellow-800 text-xs mt-1">
                    This user hasn't completed onboarding. You can exit impersonation using the button above to return to the admin dashboard.
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 1 || loading}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                currentStep === 1 || loading
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleComplete}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Completing...
                  </>
                ) : (
                  <>
                    Complete Setup
                    <Check className="w-5 h-5" />
                  </>
                )}
              </button>
            )}
          </div>

          {/* Step Indicator */}
          <div className="text-center mt-6">
            <p className="text-sm text-gray-500">
              Step {currentStep} of {totalSteps}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}